import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { ChatMessage } from '@/hooks/use-command-messages';
import { ExtendedChatMessage } from './chat-types';
import {
    collectGroupedMessageIds,
    dedupeTempUserDuplicatesWithServer,
    groupMessagesWithBranches,
    mergeMessages,
    parseFetchedMessages,
} from './message-utils';

interface UseMergedMessagesArgs {
    /** Latest fetched messages for the active session (react-query paginated, oldest-first). */
    fetchedMessages: ChatMessage[];
    isLoadingMessages: boolean;
    isFetchingMessages: boolean;
    sessionId: string | null | undefined;
    isTyping: boolean;
    streamingMessageId: string | null;
    /** Local buffer of optimistic messages created before a session id existed. Drained on merge. */
    pendingMessagesRef: MutableRefObject<ExtendedChatMessage[]>;
    setMessages: Dispatch<SetStateAction<ExtendedChatMessage[]>>;
}

/**
 * Reconciles server-fetched messages with local state (optimistic temps, streaming rows,
 * mid-stream regeneration branches). Runs in two modes:
 *
 * - **While streaming**: rebuild from server pages but preserve local streaming branches,
 *   so older paginated messages appear without wiping the in-progress assistant bubble.
 *   Skipped entirely for `temp-regen-*` streams — those are updated targeted-only by the SSE handler.
 * - **Idle**: merge fetched + local, drop temps that have been persisted (dbId match),
 *   keep temps that are still streaming or unflushed.
 */
export function useMergedMessages({
    fetchedMessages,
    isLoadingMessages,
    isFetchingMessages,
    sessionId,
    isTyping,
    streamingMessageId,
    pendingMessagesRef,
    setMessages,
}: UseMergedMessagesArgs) {
    useEffect(() => {
        const isTempRegenerating = Boolean(streamingMessageId?.startsWith('temp-regen-'));

        if (isTyping || streamingMessageId) {
            // Regeneration streams use a temporary branch id (`temp-regen-*`) that is not
            // immediately present in fetched pages. Rebuilding from fetched data here can
            // drop the in-flight/newly-finished branch before the backend catches up.
            if (isTempRegenerating) {
                return;
            }

            // Regeneration and send streams both rely on `groupedWithLocalBranches` below to
            // preserve the in-flight branch. Running the merge is needed so pagination pages
            // are still merged in while streaming.
            if (fetchedMessages.length > 0) {
                setMessages((currentMessages) => {
                    const parsed = parseFetchedMessages(fetchedMessages);
                    const grouped = groupMessagesWithBranches(parsed);
                    const serverIds = collectGroupedMessageIds(grouped);

                    // Preserve local streaming branches so regenerating messages
                    // don't disappear when server data is merged mid-stream. Also keep
                    // locally streamed content when the cache hasn't been refetched.
                    const groupedWithLocalBranches = grouped.map((serverMsg) => {
                        const localMsg = currentMessages.find((m) => m.id === serverMsg.id || m.dbId === serverMsg.id);
                        const localHasRegenTempBranch = Boolean(
                            localMsg?.branches?.some((b) => b.id.startsWith('temp-regen-')),
                        );
                        if (localHasRegenTempBranch) {
                            return {
                                ...serverMsg,
                                branches: localMsg!.branches,
                                activeBranchIndex: localMsg!.activeBranchIndex,
                            };
                        }
                        if (localMsg?.branches && localMsg.branches.some((b) => b.isStreaming)) {
                            return {
                                ...serverMsg,
                                branches: localMsg.branches,
                                activeBranchIndex: localMsg.activeBranchIndex,
                            };
                        }
                        // Local row is still actively streaming as a top-level message — keep it
                        // verbatim. Replacing with serverMsg swaps the temp id (`temp-system-*`)
                        // out from under the SSE handler, so subsequent text_delta events find
                        // nothing to update and the bubble silently goes empty. Race shows up
                        // when the GET refetch lands after message_start (which set dbId) but
                        // before the first text_delta. Overlay `parent_message_id` from the
                        // server row so the parent-anchor sort below can still pin it next
                        // to its user message — the local copy was created without one.
                        if (localMsg?.isStreaming && !localMsg.branches) {
                            return {
                                ...localMsg,
                                parent_message_id:
                                    (serverMsg as { parent_message_id?: string | null }).parent_message_id ??
                                    (localMsg as { parent_message_id?: string | null }).parent_message_id,
                            };
                        }
                        const localHasContent =
                            !!localMsg &&
                            ((localMsg.contentSegments?.length ?? 0) > 0 ||
                                (typeof localMsg.content === 'string' && localMsg.content.length > 0));
                        const serverHasContent =
                            (serverMsg.contentSegments?.length ?? 0) > 0 ||
                            (typeof serverMsg.content === 'string' && serverMsg.content.length > 0);
                        if (localHasContent && !serverHasContent) {
                            return {
                                ...localMsg!,
                                parent_message_id:
                                    (serverMsg as { parent_message_id?: string | null }).parent_message_id ??
                                    (localMsg as { parent_message_id?: string | null })?.parent_message_id,
                            };
                        }
                        // If we locally marked the stream complete but the server cache still
                        // reports GENERATING (DB update lag), trust local — otherwise a ghost
                        // streaming orb shows on the wrong bubble. Keep the local content too:
                        // the cached row is a snapshot taken mid-stream, shorter than what streamed.
                        if (
                            localMsg &&
                            localMsg.isStreaming === false &&
                            (serverMsg.isStreaming || serverMsg.status === 'GENERATING')
                        ) {
                            const base = localHasContent
                                ? {
                                      ...localMsg,
                                      parent_message_id:
                                          (serverMsg as { parent_message_id?: string | null }).parent_message_id ??
                                          (localMsg as { parent_message_id?: string | null }).parent_message_id,
                                  }
                                : serverMsg;
                            return {
                                ...base,
                                isStreaming: false,
                                status: localMsg.status === 'STOPPED' ? 'STOPPED' : 'COMPLETED',
                                ...(localMsg.suggestions?.length && !base.suggestions?.length
                                    ? { suggestions: localMsg.suggestions }
                                    : {}),
                            };
                        }
                        if (localMsg?.suggestions?.length && !serverMsg.suggestions?.length) {
                            return {
                                ...serverMsg,
                                suggestions: localMsg.suggestions,
                                ...(localMsg.contextUsage && !serverMsg.contextUsage
                                    ? { contextUsage: localMsg.contextUsage }
                                    : {}),
                            };
                        }
                        if (localMsg?.contextUsage && !serverMsg.contextUsage) {
                            return { ...serverMsg, contextUsage: localMsg.contextUsage };
                        }
                        return serverMsg;
                    });

                    const localOnly = currentMessages.filter((m) => {
                        if (serverIds.has(m.id)) return false;
                        if (m.dbId && serverIds.has(m.dbId)) return false;
                        return true;
                    });
                    const combined = [...groupedWithLocalBranches, ...localOnly];
                    // Chronological sort so paginated-in older messages slot into their correct place
                    // rather than being appended at the bottom.
                    const toMs = (m: ExtendedChatMessage): number => {
                        const raw = (m as { created_at?: string | Date }).created_at ?? m.timestamp;
                        if (raw == null) return 0;
                        const t = raw instanceof Date ? raw : new Date(raw);
                        const ms = t.getTime();
                        return Number.isFinite(ms) ? ms : 0;
                    };
                    // Temp streaming assistants are anchored to their preceding user message —
                    // server-assigned created_at on the just-persisted user can lag the local
                    // click moment, otherwise the streaming bubble would float above the user.
                    const lastPersistedUserMs = combined.reduce((max, m) => {
                        if (m.role !== 'user' || m.id.startsWith('temp-')) return max;
                        const ms = toMs(m);
                        return ms > max ? ms : max;
                    }, 0);
                    // Pair assistants to their user message by parent_message_id, not array order:
                    // a regenerated branch's created_at can post-date later user turns, so sorting
                    // purely by time would drop the grouped row below an unrelated user message.
                    const userMsByDbId = new Map<string, number>();
                    for (const m of combined) {
                        if (m.role !== 'user') continue;
                        const ms = toMs(m);
                        userMsByDbId.set(String(m.id), ms);
                        if (m.dbId) userMsByDbId.set(String(m.dbId), ms);
                    }
                    const sortMs = (m: ExtendedChatMessage): number => {
                        if (m.role === 'assistant') {
                            const parentId = (m as { parent_message_id?: string | null }).parent_message_id;
                            if (parentId) {
                                const parentMs = userMsByDbId.get(String(parentId));
                                if (parentMs != null) return parentMs + 1;
                            }
                        }
                        // A locally-streaming assistant has a client-side `new Date()` timestamp that
                        // predates the server's `created_at` for the paired user row, so sorting by
                        // raw time would float the bubble above the user. Anchor it just after the
                        // last persisted user instead. (Don't gate on a `temp-` id prefix: the id
                        // gets swapped to the real one on `message_start`, but the anchor still has
                        // to apply.)
                        if (
                            m.role === 'assistant' &&
                            m.isStreaming &&
                            lastPersistedUserMs > 0
                        ) {
                            return Math.max(toMs(m), lastPersistedUserMs + 1);
                        }
                        return toMs(m);
                    };
                    combined.sort((a, b) => sortMs(a) - sortMs(b));
                    // Bail out early if the id set didn't change — avoids a re-render during streaming.
                    if (combined.length === currentMessages.length) {
                        const currentIds = new Set(currentMessages.map((m) => m.id));
                        const combinedIds = new Set(combined.map((m) => m.id));
                        if (currentIds.size === combinedIds.size && [...combinedIds].every((id) => currentIds.has(id))) {
                            return currentMessages;
                        }
                    }
                    return dedupeTempUserDuplicatesWithServer(combined);
                });
            }
            return;
        }

        setMessages((currentMessages) => {
            const hasValidTempMessages = currentMessages.some(
                (m) => m.id.startsWith('temp-') && (m.dbId || m.isStreaming),
            );
            if (hasValidTempMessages) return currentMessages;

            if (sessionId && fetchedMessages.length === 0 && (isLoadingMessages || isFetchingMessages)) {
                return [];
            }

            if (fetchedMessages.length > 0) {
                const merged = mergeMessages(fetchedMessages, currentMessages);
                pendingMessagesRef.current = [];
                return merged;
            } else if (pendingMessagesRef.current.length > 0) {
                return pendingMessagesRef.current;
            } else if (currentMessages.length === 0 && !isLoadingMessages && !sessionId) {
                return [];
            }
            if (sessionId && !isLoadingMessages && fetchedMessages.length === 0 && currentMessages.length === 0) {
                return [];
            }
            return currentMessages;
        });
    }, [fetchedMessages, isLoadingMessages, isFetchingMessages, sessionId, isTyping, streamingMessageId, pendingMessagesRef, setMessages]);
}
